import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Map, LibraryBig, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Loading } from "../components/shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useCourseActions } from "../components/academy/useAcademy";
import { JourneyHeader, ModeBanner } from "../components/academy/JourneyHeader";
import { CompetencyMap } from "../components/academy/CompetencyMap";
import { SelfAssessmentDialog } from "../components/academy/SelfAssessment";
import { BrowseAll } from "../components/academy/BrowseAll";
import { LinkedInConnection } from "../components/academy/admin/LinkedInConnection";
import { CurationQueue } from "../components/academy/admin/CurationQueue";
import { FrameworkEditor } from "../components/academy/admin/FrameworkEditor";

export default function Academy() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "sustek_admin";
  const [tab, setTab] = useState("journey");
  const [assessOpen, setAssessOpen] = useState(false);
  const { data: journey, isLoading } = useQuery({ queryKey: ["journey"], queryFn: async () => (await api.get("/academy/journey")).data });
  const { data: framework } = useQuery({ queryKey: ["framework"], queryFn: async () => (await api.get("/academy/framework")).data });
  const actions = useCourseActions();
  const setStage = useMutation({
    mutationFn: (stage) => api.put("/academy/journey/stage", { stage }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["journey"] }); toast.success(`Stage updated — showing the ${journey?.stages?.find((s) => s.key === r.data.stage)?.name || r.data.stage} syllabus`); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  if (isLoading || !journey || !framework) return <Loading />;

  return (
    <div>
      <PageHeader eyebrow="Adviser Academy" title="Your competency-led learning journey"
        description="See the competencies expected at your career stage and the courses — from LinkedIn Learning and SusTek — that close the gap to the next one. AI recommends, admins approve, you decide." />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 bg-slate-100 rounded-xl p-1 h-auto flex-wrap justify-start" data-testid="academy-tabs">
          <TabsTrigger value="journey" data-testid="tab-journey" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"><Map className="w-4 h-4 mr-1.5" /> My journey</TabsTrigger>
          <TabsTrigger value="browse" data-testid="tab-browse" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"><LibraryBig className="w-4 h-4 mr-1.5" /> Browse all</TabsTrigger>
          {isAdmin && <TabsTrigger value="admin" data-testid="tab-admin" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"><ShieldCheck className="w-4 h-4 mr-1.5" /> Admin</TabsTrigger>}
        </TabsList>

        <TabsContent value="journey">
          <ModeBanner linkedin={journey.linkedin} isAdmin={isAdmin} onGoAdmin={() => setTab("admin")} />
          <JourneyHeader journey={journey} onSelectStage={(s) => setStage.mutate(s)} pending={setStage.isPending} onAssess={() => setAssessOpen(true)} />
          <div className="eyebrow mb-3">Competency map · {journey.stage_info?.name}</div>
          <CompetencyMap competencies={journey.competencies} actions={actions} onAssess={() => setAssessOpen(true)} />
          <SelfAssessmentDialog open={assessOpen} onClose={() => setAssessOpen(false)} journey={journey} />
        </TabsContent>

        <TabsContent value="browse">
          <ModeBanner linkedin={journey.linkedin} isAdmin={isAdmin} onGoAdmin={() => setTab("admin")} />
          <BrowseAll framework={framework} actions={actions} initialStage={journey.stage} />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="admin">
            <div className="space-y-8" data-testid="admin-panel">
              <LinkedInConnection />
              <section><div className="eyebrow mb-3">Curation queue</div><CurationQueue framework={framework} /></section>
              <section><div className="eyebrow mb-3">Competency framework</div><FrameworkEditor framework={framework} /></section>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
